/**
 * Helper to resolve document/batch class settings with a consistent precedence.
 *
 * Precedence rule (used across the app): the document class always wins over the batch
 * class. If a setting is defined on the document class its value is used — even an explicit
 * 'false'. Only if the setting is NOT defined on the document class is the batch class value
 * used as a fallback.
 * Available Settings are defined in the backend and available via
 * http://squeeze.docker.localhost/api/v2/documentClassSettings
 */

import {ClientManager} from "@/singletons/ClientManager";

/** A class setting entry (same for DocumentClassSetting and BatchClassSetting). */
interface ClassSetting {
	settingName?: string;
	value?: string;
}

/** Returns the value of the named setting within a settings list, or null if it is absent. */
const findSettingValue = (settings: ClassSetting[], settingName: string): string | null => {
	const setting = settings.find(entry => entry.settingName === settingName);
	return setting && setting.value != null ? setting.value : null;
};

/**
 * Resolves the effective value of a class setting "document class over batch class".
 *
 * @param settingName The setting to resolve (e.g. 'ZUGFeRDstrictMode').
 * @param documentClassId Optional document class id (takes precedence when the setting is defined there).
 * @param batchClassId Optional batch class id (fallback when the document class does not define the setting).
 * @returns The effective raw value, or null if the setting is defined at neither level.
 */
export const resolveClassSetting = async (
	settingName: string,
	documentClassId?: number,
	batchClassId?: number
): Promise<string | null> => {
	try {
		if (documentClassId) {
			const documentClassSettings = await ClientManager.getInstance().squeeze.documentClass.getDocumentClassSettings(documentClassId);
			const documentClassValue = findSettingValue(documentClassSettings, settingName);
			if (documentClassValue !== null) {
				return documentClassValue;
			}
		}

		if (batchClassId) {
			const batchClassSettings = await ClientManager.getInstance().squeeze.batchClass.getBatchClassSettings(batchClassId);
			return findSettingValue(batchClassSettings, settingName);
		}

		return null;
	} catch (error) {
		return null;
	}
};

/**
 * TEMPORARY: Fallback defaults for settings that the backend omits entirely when
 * they were never explicitly configured (neither 'true' nor 'false'). Because the
 * property is missing, {@link resolveClassSetting} returns null and we cannot tell
 * "not set" apart from "off" — so we apply the new product default here.
 *
 * ZUGFeRDstrictMode: the new default is enabled, so every customer has strict mode
 * active by default unless they explicitly turned it off ('false').
 *
 * Remove once the backend returns these settings with their default value.
 */
const TEMPORARY_SETTING_DEFAULTS: Record<string, boolean> = {
	ZUGFeRDstrictMode: true,
};

/**
 * Convenience wrapper around {@link resolveClassSetting} for boolean settings:
 * returns true only if the effective value is 'true'. If the setting is defined at
 * neither level (value null/undefined), the {@link TEMPORARY_SETTING_DEFAULTS}
 * fallback is used, defaulting to false when no entry exists. An explicit 'false'
 * is always respected.
 */
export const isClassSettingEnabled = async (
	settingName: string,
	documentClassId?: number,
	batchClassId?: number
): Promise<boolean> => {
	const value = await resolveClassSetting(settingName, documentClassId, batchClassId);
	if (value === null) {
		return TEMPORARY_SETTING_DEFAULTS[settingName] ?? false;
	}
	return value === 'true';
};
